import {useState} from 'react'
import {Box, Button, Chip, IconButton, Typography, Tooltip} from '@mui/material'
import {DeleteTwoTone, EditTwoTone} from '@mui/icons-material'

import withLayout from '../../hocs/Layout'

import {apiConstants, postGeneric} from '../../common/constants'
import ProzoDataGrid from '../../common/ProzoDataGrid'

import {AddPackage} from './AddPackage'
import {useGenericPostRequestQuery} from '../../redux/commonRTK'

import {getMerchant} from '../../helpers/ReduxHelpers'

import {TableSkeleton} from '../../common/Skeletons'
import {formatDate} from '../../helpers/UtilityHelper'

import ConfirmDialog from '../../common/ConfirmDialog'
import {toast} from 'react-toastify'
import PageTitle from '../../common/PageTitle'

import {pageContainerSx, filterBarSx, primaryBtnSx, dataGridWrapSx} from '../../styles/patterns'
import {primaryTextSx, secondaryTextSx, cellStackSx} from '../orders/list2/cellStyles'
import {SUCCESS, ERROR} from '../../styles/foundation/colors'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'

function PackageComponent() {
  const [page, setPage] = useState(1)
  const [sizePerPage, setSizePerPage] = useState(10)
  const [selectedPackage, setSelectedPackage] = useState(null)

  const [isAddPackageOpen, setIsAddPackageOpen] = useState(false)
  const [packageToDelete, setPackageToDelete] = useState(null)

  const hookOptions = {
    url: apiConstants.GET_PACKAGES,
    method: 'GET',
    data: {
      merchantOid: getMerchant().id,
    },
  }

  const {data: packages, isLoading, refetch} = useGenericPostRequestQuery(hookOptions)

  const getPackageStatusChipSx = (status) => {
    const color = status === 'ACTIVE' ? SUCCESS : ERROR
    return {
      fontFamily: FONT,
      fontWeight: WEIGHT.semibold,
      fontSize: 11,
      height: 24,
      bgcolor: color + '18',
      color,
      border: `1px solid ${color}40`,
      '& .MuiChip-label': {px: 1},
    }
  }

  const columns = [
    {
      field: 'box_name',
      headerName: 'Name',
      flex: 1.5,
      minWidth: 150,
      renderCell: ({row}) => (
        <Box sx={cellStackSx}>
          <Typography sx={primaryTextSx}>{row.box_name}</Typography>
        </Box>
      ),
    },
    {
      field: 'height_cm',
      headerName: 'Height (cm)',
      flex: 1,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({row}) => (
        <Box sx={cellStackSx}>
          <Typography sx={primaryTextSx}>{row.height_cm}</Typography>
        </Box>
      ),
    },
    {
      field: 'breadth_cm',
      headerName: 'Breadth (cm)',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({row}) => (
        <Box sx={cellStackSx}>
          <Typography sx={primaryTextSx}>{row.breadth_cm}</Typography>
        </Box>
      ),
    },
    {
      field: 'length_cm',
      headerName: 'Length (cm)',
      flex: 1,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({row}) => (
        <Box sx={cellStackSx}>
          <Typography sx={primaryTextSx}>{row.length_cm}</Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({row}) => (
        <Chip
          label={row.status}
          size='small'
          sx={getPackageStatusChipSx(row.status)}
        />
      ),
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({row}) => (
        <Box sx={cellStackSx}>
          <Typography sx={secondaryTextSx}>{formatDate(row.createdDate)}</Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({row}) => (
        <>
          <Tooltip title='Update Package Details'>
            <IconButton size='small' color='primary' onClick={() => setSelectedPackage(row)}>
              <EditTwoTone fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Delete Package'>
            <IconButton size='small' color='error' onClick={() => setPackageToDelete(row)}>
              <DeleteTwoTone fontSize='small' />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ]

  const handleClose = () => {
    setSelectedPackage(null)
    setIsAddPackageOpen(false)
  }

  const handleDeletePackage = async () => {
    try {
      await postGeneric(`${apiConstants.DELETE_PACKAGE}?id=${packageToDelete.id}`)
      toast.success('Package deleted successfully')
      refetch()
      setPackageToDelete(null)
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting package')
    }
  }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Define reusable box dimensions for your shipments'>Packages</PageTitle>
      <ConfirmDialog
        title='Are you sure you want to delete this package?'
        open={!!packageToDelete}
        setOpen={setPackageToDelete}
        onConfirm={handleDeletePackage}
        onReject={() => setPackageToDelete(null)}
      >
        Are you sure you want to delete this package ({packageToDelete?.box_name})?
      </ConfirmDialog>
      <AddPackage
        open={isAddPackageOpen || !!selectedPackage}
        refetch={refetch}
        onClose={handleClose}
        selectedPackage={selectedPackage}
      />
      <Box sx={filterBarSx}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.sm}}>
            All Packages
          </Typography>
          <Button
            size='small'
            variant='contained'
            disableElevation
            onClick={() => setIsAddPackageOpen(true)}
            sx={primaryBtnSx}
          >
            Add Package
          </Button>
        </Box>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <Box sx={dataGridWrapSx}>
            <ProzoDataGrid
              columns={columns}
              autoHeight={true}
              rows={packages || []}
              sizePerPage={sizePerPage}
              setSizePerPage={setSizePerPage}
              setPage={setPage}
              page={page}
              pagination={true}
              hideFooterPagination={false}
              filterMode='server'
              rowCount={packages?.length || 0}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export function Package() {
  const Component = withLayout(PackageComponent)
  return <Component />
}
